import { useState, useEffect, useMemo } from 'react';
import { useGuests, useAttendanceMutation } from '../api';
import { getFloorplanUrl } from './PhotoGallery/utils/photoHelpers';
import ImageModal from './ImageModal';
import { StatusBadge, ActionButton, Button } from './ui';


const Venue = ({ customFloorplan, customMenu, venueTitle, venueSubtitle }) => {
  // Dynamic page title based on whether menu is configured
  const pageTitle = venueTitle || (customMenu ? 'Venue & Menu' : 'Venue');
  const pageSubtitle = venueSubtitle || 'Find your table and confirm your attendance.';
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [attendanceMessage, setAttendanceMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMenuModalOpen, setIsMenuModalOpen] = useState(false);

  // Helper to detect if URL is a PDF
  const isPdf = (url) => {
    if (!url) return false;
    return url.toLowerCase().endsWith('.pdf') || url.startsWith('data:application/pdf');
  };

  // Use React Query hooks for data fetching
  const {
    data: guests = [],
    isLoading: guestsLoading,
    error: guestsError,
    refetch: refetchGuests
  } = useGuests();

  const attendanceMutation = useAttendanceMutation();

  // Combined loading and error states
  const loading = guestsLoading;
  const error = guestsError;

  // Helper function to extract guest data consistently
  // Backend now returns fixed structure: name, table, attended
  const getGuestData = (guest) => ({
    name: guest.name || '',
    table: guest.table || ''
  });

  // SVG Icon Constants
  const VenueIcon = () => (
    <svg className="mx-auto h-16 w-16 text-primary-40 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
    </svg>
  );

  // Reusable component for floorplan unavailable state
  const FloorplanUnavailable = ({ title = "Floorplan Coming Soon", message = "The venue floorplan will be available closer to the wedding date." }) => (
    <div className="bg-surface rounded-xl shadow-sm border border-accent-70 p-6 sm:p-8 text-center">
      <VenueIcon />
      <h3 className="text-lg font-medium text-secondary mb-2">
        {title}
      </h3>
      <p className="text-secondary-70">
        {message}
      </p>
    </div>
  );

  // Helper function to extract user-friendly error message
  const getErrorMessage = (error) => {
    if (!error) return 'An unknown error occurred';

    // Handle Axios errors with response
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.response.statusText || '';

      if (status === 401) {
        return 'Unauthorized. Please log in again.';
      } else if (status === 403 || status === 503) {
        return 'Access denied to guest list. Please verify the spreadsheet configuration.';
      } else if (status === 404) {
        return 'Guest list not found.';
      } else if (status === 429) {
        return 'Too many requests. Please try again later.';
      } else if (status === 500) {
        return 'Server error. Please try again later.';
      } else if (typeof message === 'string' && message) {
        return message;
      }
      
      return `Failed to load guest list (Error ${status})`;
    }

    // Handle network errors
    if (error.request) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }

    // Handle error message string
    if (typeof error.message === 'string' && error.message) {
      return error.message;
    }

    // Fallback for other error types
    return 'An error occurred while loading the guest list. Please try again.';
  };

  // Handle authentication errors (401 = unauthorized, should logout)
  useEffect(() => {
    if (guestsError?.response?.status === 401) {
      localStorage.removeItem('weddingToken');
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    }
  }, [guestsError]);

  // Handle manual refresh of guest data
  const handleRefreshGuests = async () => {
    try {
      await refetchGuests();
    } catch (error) {
      console.error('Failed to refresh guest data:', error);
    }
  };

  // Get floorplan URL - use custom floorplan if provided, otherwise use default
  const floorplanUrl = customFloorplan || getFloorplanUrl();

  // Optimized search functionality with smart relevance sorting
  const filteredGuestsList = useMemo(() => {
    if (!searchTerm.trim()) {
      return guests;
    }

    const search = searchTerm.toLowerCase().trim();
    
    // Filter and score matches
    const scored = guests
      .map(guest => {
        const { name, table } = getGuestData(guest);
        const nameLower = name.toLowerCase();
        const tableLower = table.toString().toLowerCase();
        
        // Calculate relevance score (lower is better)
        let score = Infinity;
        
        if (tableLower === search) score = 0;
        else if (tableLower.startsWith(search)) score = 1;
        else if (nameLower === search) score = 2;
        else if (nameLower.startsWith(search)) score = 3;
        else if (tableLower.includes(search)) score = 4 + tableLower.length;
        else if (nameLower.includes(search)) score = 100;
        
        return { guest, score };
      })
      .filter(({ score }) => score !== Infinity);
    
    return scored
      .sort((a, b) => {
        if (a.score !== b.score) return a.score - b.score;
        return getGuestData(a.guest).name.localeCompare(getGuestData(b.guest).name);
      })
      .map(({ guest }) => guest);
  }, [searchTerm, guests]);

  // Handle guest selection
  const handleGuestSelect = (guest) => {
    setSelectedGuest(guest);
    setSearchTerm('');
  };

  // Handle attendance registration using React Query mutation
  const handleAttendance = async (attended) => {
    if (!selectedGuest) return;

    setAttendanceMessage('');

    const { name: guestName, table: tableNumber } = getGuestData(selectedGuest);

    try {
      await attendanceMutation.mutateAsync({
        guestName,
        tableNumber,
        attended
      });

      setAttendanceMessage(
        attended
          ? `✅ Attendance confirmed for ${guestName}!`
          : `❌ Attendance cancelled for ${guestName}.`
      );

      setSelectedGuest(prev => prev ? { ...prev, attended: attended ? 'Y' : 'N' } : null);
      setTimeout(() => setAttendanceMessage(''), 3000);

    } catch (err) {
      console.error('Attendance error:', err);
      setAttendanceMessage('❌ Failed to update attendance. Please try again.');
      setTimeout(() => setAttendanceMessage(''), 5000);
    }
  };

  // Clear search and selection
  const clearSearch = () => {
    setSearchTerm('');
    setSelectedGuest(null);
  };

  // Modal handlers
  const openFloorplanModal = () => setIsModalOpen(true);
  const closeFloorplanModal = () => setIsModalOpen(false);

  // Skeleton loading component
  const SkeletonLoader = () => (
    <div className="w-full bg-gradient-to-b from-accent-20 to-accent-10 min-h-screen">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="text-center mb-8">
          <div className="h-8 bg-primary-20 rounded-lg w-64 mx-auto mb-2 animate-pulse"></div>
          <div className="h-4 bg-primary-10 rounded w-48 mx-auto animate-pulse"></div>
        </div>
        <div className="bg-surface rounded-xl shadow-sm border border-accent-50 p-4 sm:p-6 mb-6">
          <div className="space-y-4">
            <div className="h-4 bg-primary-10 rounded w-48 animate-pulse"></div>
            <div className="relative">
              <div className="w-full h-12 bg-primary-10 rounded-lg animate-pulse"></div>
            </div>
          </div>
        </div>
        <div className="bg-surface rounded-xl shadow-sm border border-accent-50 p-4 sm:p-6">
          <div className="h-6 bg-primary-20 rounded w-32 mb-4 animate-pulse"></div>
          <div className="aspect-video bg-primary-10 rounded-lg animate-pulse"></div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <SkeletonLoader />;
  }

  if (error) {
    const errorMessage = getErrorMessage(error);
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="text-center max-w-md">
          <svg className="mx-auto h-16 w-16 text-red-500 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
          </svg>
          <h2 className="text-xl font-semibold text-secondary mb-3">Error Loading Venue</h2>
          <p className="text-secondary-70 mb-6 leading-relaxed">{errorMessage}</p>
          <Button
            onClick={() => window.location.reload()}
            variant="primary"
            size="lg"
            icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gradient-to-b from-accent-20 to-accent-10">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mb-2">
            <h1 className="text-2xl font-semibold text-secondary">
              {pageTitle}
            </h1>
          </div>
          <p className="text-secondary-70">
            {pageSubtitle}
          </p>
          <p className="text-secondary-60 text-sm mt-1">
            If you are having issues, please proceed to the reception and ask for assistance.
          </p>
        </div>

        {/* Search Section */}
        <div className="bg-surface-50 rounded-xl shadow-sm border border-accent-50 p-4 sm:p-6 mb-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label htmlFor="guest-search" className="text-sm font-medium text-secondary">
                Guest List & Table Numbers
              </label>
              <button
                onClick={handleRefreshGuests}
                disabled={guestsLoading}
                className="p-2 text-secondary hover:text-primary hover:bg-accent-20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Refresh guest list"
                aria-label="Refresh guest list"
              >
                <svg className={`w-5 h-5 ${guestsLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>

            {/* Search Input */}
            <div className="relative">
              <input
                id="guest-search"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Enter your name or table number..."
                className={`w-full px-4 py-3 bg-accent-10 text-xs border border-accent-10 rounded-lg focus:ring-2 focus:ring-primary-50 focus:border-accent transition-colors placeholder-secondary-60 ${
                  (searchTerm || selectedGuest) ? 'pr-12' : 'pr-10'
                }`}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center space-x-2">
                <svg className="w-5 h-5 text-primary-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {(searchTerm || selectedGuest) && (
                  <button
                    onClick={clearSearch}
                    className="p-1 text-primary-60 hover:text-primary hover:bg-primary-10 rounded transition-colors"
                    aria-label="Clear search"
                    title="Clear search"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Search Results */}
          {searchTerm && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-secondary mb-2">
                Search Results ({filteredGuestsList.length})
              </h3>
              <div className="max-h-80 sm:max-h-48 overflow-y-auto bg-accent-20 border border-accent-20 rounded-lg custom-scrollbar">
                {filteredGuestsList.length > 0 ? (
                  filteredGuestsList.map((guest) => (
                    <button
                      key={guest.id}
                      onClick={() => handleGuestSelect(guest)}
                      className="w-full text-left px-4 py-3 hover:bg-primary-5 border-b border-accent-20 last:border-b-0 transition-colors group"
                      title={getGuestData(guest).name}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                        <span className="font-medium text-secondary truncate min-w-0 flex-1" title={getGuestData(guest).name}>
                          {getGuestData(guest).name}
                        </span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <StatusBadge type="table" tableNumber={getGuestData(guest).table} />
                          {guest.attended === 'Y' ? (
                            <StatusBadge type="confirmed" />
                          ) : (
                            <StatusBadge type="notConfirmed" />
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-secondary-60 text-center">
                    No guests found matching "{searchTerm}"
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Selected Guest */}
          {selectedGuest && !searchTerm && (
            <div className="mt-4 p-4 bg-primary-10 border border-accent-50 rounded-lg">
              <div className="flex flex-col gap-3">
                <h3 className="font-medium text-secondary text-base">
                  {getGuestData(selectedGuest).name}
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge type="table" tableNumber={getGuestData(selectedGuest).table} />
                  {selectedGuest.attended === 'Y' ? (
                    <>
                      <StatusBadge type="confirmed" />
                      <ActionButton
                        type="cancelAttendance"
                        onClick={() => handleAttendance(false)}
                        disabled={attendanceMutation.isPending}
                        isLoading={attendanceMutation.isPending}
                      />
                    </>
                  ) : (
                    <ActionButton
                      type="confirmAttendance"
                      onClick={() => handleAttendance(true)}
                      disabled={attendanceMutation.isPending}
                      isLoading={attendanceMutation.isPending}
                    />
                  )}
                </div>
              </div>
              {attendanceMessage && (
                <div className="mt-3 p-2 bg-surface-80 rounded text-xs text-secondary">
                  {attendanceMessage}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Floorplan Display */}
        {floorplanUrl ? (
          <div className="bg-surface rounded-xl shadow-sm border border-accent-50 p-4 sm:p-6">
            <h2 className="text-lg font-medium text-secondary mb-4 text-center">
              Venue Floorplan
            </h2>
            <div className="flex justify-center">
              <div className="relative group cursor-pointer" onClick={openFloorplanModal}>
                <img
                  src={floorplanUrl}
                  alt="Wedding venue floorplan"
                  className="max-w-full h-auto rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                  title="Click to view full-size floorplan"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black-10 rounded-lg transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
                  <div className="bg-surface-90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                    <svg className="w-6 h-6 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
                <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  <div className="bg-black-60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded">
                    Click to enlarge
                  </div>
                </div>
              </div>
              <div className="hidden">
                <FloorplanUnavailable
                  title="Floorplan Not Available"
                  message="Floorplan image not available"
                />
              </div>
            </div>
          </div>
        ) : (
          <FloorplanUnavailable />
        )}

        {/* Floorplan Modal */}
        <ImageModal
          isOpen={isModalOpen}
          onClose={closeFloorplanModal}
          imageUrl={floorplanUrl}
          imageAlt="Wedding venue floorplan - full size"
          title="Wedding Venue Floorplan"
        />

        {/* Menu Section */}
        {customMenu && (
          <>
            {isPdf(customMenu) ? (
              <div className="bg-surface rounded-xl shadow-sm border border-accent-50 p-4 sm:p-6 mt-6">
                <h2 className="text-lg font-medium text-secondary mb-4 text-center">Menu</h2>
                <div className="flex flex-col items-center">
                  <div className="bg-accent-20 rounded-lg p-6 w-full max-w-md">
                    <div className="flex flex-col items-center text-center">
                      <svg className="w-16 h-16 text-primary-60 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 3v6a1 1 0 001 1h6" />
                      </svg>
                      <p className="text-secondary-70 mb-4">View our menu</p>
                      <button
                        onClick={() => setIsMenuModalOpen(true)}
                        className="px-6 py-3 bg-primary text-surface rounded-lg font-medium hover:opacity-90 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View Menu
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-surface rounded-xl shadow-sm border border-accent-50 p-4 sm:p-6 mt-6">
                <h2 className="text-lg font-medium text-secondary mb-4 text-center">Menu</h2>
                <div className="flex justify-center">
                  <div className="relative group cursor-pointer" onClick={() => setIsMenuModalOpen(true)}>
                    <img
                      src={customMenu}
                      alt="menu"
                      className="max-w-full h-auto rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                      loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; }}
                      title="Click to view full-size menu"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black-10 rounded-lg transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
                      <div className="bg-surface-90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                        <svg className="w-6 h-6 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                    </div>
                    <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                      <div className="bg-black-60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded">
                        Click to enlarge
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Menu Modal - Image uses ImageModal, PDF uses custom modal */}
            {!isPdf(customMenu) ? (
              <ImageModal
                isOpen={isMenuModalOpen}
                onClose={() => setIsMenuModalOpen(false)}
                imageUrl={customMenu}
                imageAlt="Wedding menu - full size"
                title="Wedding Menu"
              />
            ) : (
              isMenuModalOpen && (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"
                  onClick={() => setIsMenuModalOpen(false)}
                >
                  <div className="relative w-full h-full flex items-center justify-center p-4" onClick={e => e.stopPropagation()}>
                    {/* Close button - same style as ImageModal */}
                    <button
                      onClick={() => setIsMenuModalOpen(false)}
                      aria-label="Close menu viewer"
                      className="absolute top-4 right-4 z-10 group"
                    >
                      <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white-20 backdrop-blur-md border border-white-20 shadow-lg transition-all duration-200 group-hover:bg-white-30 group-hover:scale-105">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </span>
                    </button>

                    {/* PDF Viewer */}
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl overflow-hidden" style={{ height: '85vh' }}>
                      <object
                        data={customMenu}
                        type="application/pdf"
                        className="w-full h-full"
                      >
                        <iframe
                          src={customMenu}
                          className="w-full h-full border-0"
                          title="Wedding Menu PDF"
                        />
                      </object>
                    </div>

                    {/* Title overlay - same style as ImageModal */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
                      <div className="bg-black-60 backdrop-blur-md px-4 py-2 rounded-lg">
                        <p className="text-white text-center font-medium">Wedding Menu</p>
                      </div>
                    </div>
                  </div>
                </div>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Venue;
